import { useMemo, useState } from 'react';
import GridLayout, { Layout, LayoutItem, WidthProvider } from 'react-grid-layout/legacy';
import { Dashboard } from '../types/Dashboard';
import { UpdateDashboardRequest } from '../types/DashboardRequests';
import { DashboardLayout, LayoutSlot } from '../types/Layout';
import { DashboardCard } from './DashboardCard';
import { EmptySlot } from './EmptySlot';
import {
  resolveSlotDashboards,
  widthToLayoutSize,
  GRID_COLS,
  GRID_ROW_HEIGHT,
  SIZE_TO_W,
  SIZE_TO_H,
} from '../utils/layoutHelpers';
import { useDashboardDrag } from '../state/DashboardDragContext';
import { cn } from '@/infrastructure/lib/utils';

const ResponsiveGrid = WidthProvider(GridLayout);

interface DashboardGridProps {
  dashboards: Dashboard[];
  onUpdateDashboard: (params: { dashboardId: string; request: UpdateDashboardRequest }) => void;
  onRemoveDashboard: (dashboardId: string) => void;
  onReorderDashboards?: (dashboards: Dashboard[]) => void;
  activeLayout?: DashboardLayout;
  onUpdateLayoutSlots?: (layoutId: string, slots: LayoutSlot[]) => void;
  onSlotPlusClick?: (slot: LayoutSlot) => void;
  onDropDashboardOnSlot?: (slotId: string, dashboardBaseId: string) => void;
  onRemoveSlot?: (slotId: string) => void;
  onUnassignSlot?: (slotId: string) => void;
  isLayoutEditable?: boolean;
}

interface RenderItem {
  key: string;
  kind: 'dashboard' | 'empty';
  dashboard?: Dashboard;
  slot?: LayoutSlot;
}

export const DashboardGrid = ({
  dashboards,
  onUpdateDashboard,
  onRemoveDashboard,
  onReorderDashboards,
  activeLayout,
  onUpdateLayoutSlots,
  onSlotPlusClick,
  onDropDashboardOnSlot,
  onRemoveSlot,
  onUnassignSlot,
  isLayoutEditable,
}: DashboardGridProps) => {
  const { drag } = useDashboardDrag();
  const [internalDragActive, setInternalDragActive] = useState(false);

  const externalDragActive = drag?.externalDashboardBaseId !== undefined;
  const wireframeActive = internalDragActive || externalDragActive;

  const sortedDashboards = useMemo(() => {
    return [...dashboards].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [dashboards]);

  const layoutMode = activeLayout !== undefined;

  const { renderItems, gridLayout } = useMemo(() => {
    if (layoutMode && activeLayout) {
      const { assignmentsBySlot, unassignedDashboards } = resolveSlotDashboards(
        activeLayout,
        sortedDashboards
      );
      const items: RenderItem[] = [];
      const layoutItems: LayoutItem[] = [];
      let maxBottom = 0;
      for (const slot of activeLayout.slots) {
        const dashboard = assignmentsBySlot.get(slot.id);
        if (dashboard) {
          items.push({ key: slot.id, kind: 'dashboard', dashboard, slot });
        } else {
          items.push({ key: slot.id, kind: 'empty', slot });
        }
        // Tiles podem encolher mais que outros tipos (single-value KPIs cabem
        // confortavelmente em 2 rows). Graphs/tables precisam de mais espaço.
        const isTile = slot.formFactor === 'tile';
        layoutItems.push({
          i: slot.id,
          x: slot.x,
          y: slot.y,
          w: slot.w,
          h: slot.h,
          minW: isTile ? 2 : 3,
          minH: isTile ? 2 : 3,
          isDraggable: isLayoutEditable,
          isResizable: isLayoutEditable,
        });
        maxBottom = Math.max(maxBottom, slot.y + slot.h);
      }
      // Dashboards sem slot (itens > slots, ex: adicionados além da capacidade
      // do layout) seriam invisíveis se renderizássemos só os slots. Empacota-os
      // abaixo do grid como itens estáticos para que nada fique escondido — o
      // usuário pode então arrastá-los para slots ou crescer o layout.
      let x = 0;
      let y = maxBottom;
      let rowHeight = 0;
      for (const d of unassignedDashboards) {
        const w = SIZE_TO_W[d.size || 'medium'];
        const h = SIZE_TO_H[d.size || 'medium'];
        if (x + w > GRID_COLS) {
          x = 0;
          y += rowHeight || h;
          rowHeight = 0;
        }
        items.push({ key: d.id, kind: 'dashboard', dashboard: d });
        layoutItems.push({ i: d.id, x, y, w, h, minW: 3, minH: 3, static: true });
        x += w;
        rowHeight = Math.max(rowHeight, h);
      }
      return { renderItems: items, gridLayout: layoutItems };
    }

    const layoutItems: LayoutItem[] = [];
    let x = 0;
    let y = 0;
    let rowHeight = 0;
    for (const d of sortedDashboards) {
      const w = SIZE_TO_W[d.size || 'medium'];
      const h = SIZE_TO_H[d.size || 'medium'];
      if (x + w > GRID_COLS) {
        x = 0;
        y += rowHeight || h;
        rowHeight = 0;
      }
      layoutItems.push({ i: d.id, x, y, w, h, minW: 3, minH: 3 });
      x += w;
      rowHeight = Math.max(rowHeight, h);
    }
    return {
      renderItems: sortedDashboards.map<RenderItem>((d) => ({
        key: d.id,
        kind: 'dashboard',
        dashboard: d,
      })),
      gridLayout: layoutItems,
    };
  }, [layoutMode, activeLayout, sortedDashboards, isLayoutEditable]);

  const handleDragOrResizeStop = (newLayout: Layout) => {
    setInternalDragActive(false);

    if (layoutMode && activeLayout && onUpdateLayoutSlots) {
      const slotsById = new Map(activeLayout.slots.map((s) => [s.id, s]));
      const renderedIds = new Set<string>();
      // Only real slots become slots — the appended unassigned-dashboard items
      // (keyed by dashboard id, static) must not be adopted as slots here.
      const nextSlots: LayoutSlot[] = newLayout
        .filter((l) => slotsById.has(l.i))
        .map((l) => {
          renderedIds.add(l.i);
          const existing = slotsById.get(l.i);
          return {
            id: l.i,
            formFactor: existing?.formFactor ?? 'graph',
            size: widthToLayoutSize(l.w),
            x: l.x,
            y: l.y,
            w: l.w,
            h: l.h,
          };
        });
      for (const slot of activeLayout.slots) {
        if (!renderedIds.has(slot.id)) nextSlots.push(slot);
      }
      onUpdateLayoutSlots(activeLayout.id, nextSlots);
      return;
    }

    if (!onReorderDashboards) return;
    const sorted = [...newLayout].sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));
    const fullList: Dashboard[] = [];
    sorted.forEach((l, i) => {
      const dashboard = dashboards.find((d) => d.id === l.i);
      if (!dashboard) return;
      fullList.push({
        ...dashboard,
        position: i,
        size: widthToLayoutSize(l.w),
      });
    });
    if (fullList.length > 0) {
      onReorderDashboards(fullList);
    }
  };

  return (
    <div className={cn('relative', wireframeActive && 'dashboard-grid-wireframe-active')}>
      <ResponsiveGrid
        className="layout"
        layout={gridLayout}
        cols={GRID_COLS}
        rowHeight={GRID_ROW_HEIGHT}
        margin={[16, 16]}
        compactType="vertical"
        draggableHandle=".dashboard-drag-handle"
        isDraggable={!layoutMode || isLayoutEditable}
        isResizable={!layoutMode || isLayoutEditable}
        onDragStart={() => setInternalDragActive(true)}
        onDragStop={handleDragOrResizeStop}
        onResizeStart={() => setInternalDragActive(true)}
        onResizeStop={handleDragOrResizeStop}
      >
        {renderItems.map((item) =>
          item.kind === 'dashboard' && item.dashboard ? (
            <div key={item.key}>
              <DashboardCard
                dashboard={item.dashboard}
                onUpdate={onUpdateDashboard}
                onRemove={onRemoveDashboard}
                onUnassignSlot={
                  item.slot && onUnassignSlot
                    ? () => onUnassignSlot(item.slot!.id)
                    : undefined
                }
                onRemoveSlot={
                  item.slot && isLayoutEditable && onRemoveSlot
                    ? () => onRemoveSlot(item.slot!.id)
                    : undefined
                }
              />
            </div>
          ) : item.slot ? (
            <div key={item.key}>
              <EmptySlot
                slot={item.slot}
                onPlusClick={onSlotPlusClick ?? (() => undefined)}
                onDropDashboard={onDropDashboardOnSlot ?? (() => undefined)}
                onRemoveSlot={isLayoutEditable && onRemoveSlot ? onRemoveSlot : undefined}
              />
            </div>
          ) : null
        )}
      </ResponsiveGrid>
    </div>
  );
};
